/**
 * Customer Dashboard Page
 * Combined Orders & Purchases with 4 tabs:
 * Orders | Downloads | Keys | Subscriptions
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { BottomNavigation } from '../../components/customer';
import { useTranslation } from '../../hooks/useTranslation';
import { useAuth } from '../../context/AuthContext';

interface Order {
  id: number;
  order_number: string;
  product_name: string;
  product_image: string | null;
  product_type: string;
  total_amount: number;
  status: string;
  payment_status: string;
  created_at: string;
  has_physical_products: boolean;
  has_digital_products: boolean;
}

interface Download {
  id: number;
  productId: number;
  productName: string;
  productImage: string | null;
  orderNumber: string;
  expiresAt: string | null;
  isExpired: boolean;
  canDownload: boolean;
  createdAt: string;
}

interface ProductKey {
  id: number;
  productId: number;
  productName: string;
  productImage: string | null;
  orderNumber: string;
  keyValue: string;
  isRevealed: boolean;
  createdAt: string;
}

interface Subscription {
  id: number;
  productId: number;
  productName: string;
  productImage: string | null;
  orderNumber: string;
  startDate: string;
  expiryDate: string;
  status: string;
  daysRemaining: number;
  isExpired: boolean;
  isExpiringSoon: boolean;
  renewalPrice: number;
  canRenew: boolean;
}

type TabType = 'orders' | 'downloads' | 'keys' | 'subscriptions';

const Dashboard: React.FC = () => {
  const { botUsername } = useParams<{ botUsername: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { t } = useTranslation();
  const { token: authToken } = useAuth();

  const initialTab = (searchParams.get('tab') as TabType) || 'orders';
  const [activeTab, setActiveTab] = useState<TabType>(initialTab);
  const [isLoading, setIsLoading] = useState(true);

  const [orders, setOrders] = useState<Order[]>([]);
  const [downloads, setDownloads] = useState<Download[]>([]);
  const [keys, setKeys] = useState<ProductKey[]>([]);
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);

  const currency = { symbol: '$', code: 'USD' };
  const baseUrl = process.env.REACT_APP_API_URL || '';

  const loadTabData = useCallback(async (tab: TabType) => {
    setIsLoading(true);
    try {
      const token = authToken || localStorage.getItem('auth_token');
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      const baseUrl = process.env.REACT_APP_API_URL || '';

      switch (tab) {
        case 'orders':
          const ordersRes = await fetch(`${baseUrl}/api/orders/${botUsername}`, { headers });
          const ordersData = await ordersRes.json();
          if (ordersData.success) setOrders(ordersData.data);
          break;

        case 'downloads':
          const downloadsRes = await fetch(`${baseUrl}/api/downloads/${botUsername}/my-downloads`, { headers });
          const downloadsData = await downloadsRes.json();
          if (downloadsData.success) setDownloads(downloadsData.data);
          break;

        case 'keys':
          const keysRes = await fetch(`${baseUrl}/api/downloads/${botUsername}/my-keys`, { headers });
          const keysData = await keysRes.json();
          if (keysData.success) setKeys(keysData.data);
          break;

        case 'subscriptions':
          const subsRes = await fetch(`${baseUrl}/api/subscriptions/${botUsername}/my-subscriptions`, { headers });
          const subsData = await subsRes.json();
          if (subsData.success) setSubscriptions(subsData.data);
          break;
      }
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setIsLoading(false);
    }
  }, [botUsername, authToken]);

  useEffect(() => {
    loadTabData(activeTab);
  }, [activeTab, loadTabData]);

  const handleTabChange = (tab: TabType) => {
    setActiveTab(tab);
    setSearchParams({ tab });
    window.Telegram?.WebApp?.HapticFeedback?.impactOccurred('light');
  };

  const handleOrderClick = (orderId: number) => {
    navigate(`/shop/${botUsername}/orders/${orderId}`);
  };

  const handleDownload = async (downloadId: number, productName?: string) => {
    const tg = window.Telegram?.WebApp;
    const token = authToken || localStorage.getItem('auth_token');
    const baseUrl = process.env.REACT_APP_API_URL || '';

    if (!token) {
      tg?.showAlert?.('Please log in to download');
      return;
    }

    try {
      // Show loading feedback
      tg?.HapticFeedback?.impactOccurred('medium');

      // Send file to user's Telegram chat (like native bot does)
      const response = await fetch(`${baseUrl}/api/downloads/${botUsername}/send-to-chat/${downloadId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Download failed');
      }

      // File sent to chat successfully
      tg?.showAlert?.('File sent to your Telegram chat!');
      tg?.HapticFeedback?.notificationOccurred('success');

    } catch (error: any) {
      console.error('Download error:', error);
      tg?.showAlert?.(error.message || 'Download failed. Please try from the bot.');
      tg?.HapticFeedback?.notificationOccurred('error');
    }
  };

  const handleRevealKey = async (keyId: number) => {
    try {
      const token = authToken || localStorage.getItem('auth_token');
      const baseUrl = process.env.REACT_APP_API_URL || '';

      const res = await fetch(`${baseUrl}/api/downloads/${botUsername}/keys/${keyId}/reveal`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await res.json();

      if (data.success) {
        setKeys(prev => prev.map(k =>
          k.id === keyId ? { ...k, keyValue: data.data.keyValue, isRevealed: true } : k
        ));
      }
    } catch (error) {
      console.error('Reveal key error:', error);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    window.Telegram?.WebApp?.HapticFeedback?.notificationOccurred('success');
    window.Telegram?.WebApp?.showAlert(t('keys.copiedToClipboard'));
  };

  const handleRenewSubscription = async (subscriptionId: number) => {
    try {
      const token = authToken || localStorage.getItem('auth_token');
      const baseUrl = process.env.REACT_APP_API_URL || '';

      const res = await fetch(`${baseUrl}/api/subscriptions/${botUsername}/${subscriptionId}/renew`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await res.json();

      if (data.success) {
        if (data.data.requiresPayment) {
          navigate(`/shop/${botUsername}/checkout?order=${data.data.orderId}`);
        } else {
          window.Telegram?.WebApp?.showAlert(t('subscriptions.renewSuccess'));
          loadTabData('subscriptions');
        }
      }
    } catch (error) {
      console.error('Renewal error:', error);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'pending': return '#FFA000';
      case 'processing': return '#2196F3';
      case 'shipped': return '#9C27B0';
      case 'delivered': return '#4CAF50';
      case 'cancelled': return '#f44336';
      case 'active': return '#4CAF50';
      case 'expired': return '#f44336';
      default: return '#999';
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const tabs = [
    { id: 'orders' as TabType, label: t('dashboard.orders'), count: orders.length },
    { id: 'downloads' as TabType, label: t('dashboard.downloads'), count: downloads.length },
    { id: 'keys' as TabType, label: t('dashboard.keys'), count: keys.length },
    { id: 'subscriptions' as TabType, label: t('dashboard.subs'), count: subscriptions.length },
  ];

  return (
    <PageContainer>
      <Header>
        <Title>{t('dashboard.title')}</Title>
      </Header>

      <TabsContainer>
        {tabs.map((tab) => (
          <Tab
            key={tab.id}
            $active={activeTab === tab.id}
            onClick={() => handleTabChange(tab.id)}
          >
            {tab.label}
            {tab.count > 0 && <TabBadge>{tab.count}</TabBadge>}
          </Tab>
        ))}
      </TabsContainer>

      <Content>
        {isLoading ? (
          <LoadingContainer>
            <Spinner />
          </LoadingContainer>
        ) : (
          <>
            {activeTab === 'orders' && (
              <TabContent>
                {orders.length === 0 ? (
                  <EmptyState>
                    <EmptyIcon>
                      <svg width="48" height="48" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-5 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z" />
                      </svg>
                    </EmptyIcon>
                    <EmptyText>{t('orders.noOrders')}</EmptyText>
                  </EmptyState>
                ) : (
                  <ItemList>
                    {orders.map((order) => (
                      <OrderCard key={order.id} onClick={() => handleOrderClick(order.id)}>
                        <OrderCardContent>
                          <CardImage>
                            {order.product_image ? (
                              <img src={order.product_image} alt={order.product_name} />
                            ) : (
                              <PlaceholderIcon>
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                                  <path d="M19 5v14H5V5h14m0-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-4.86 8.86l-3 3.87L9 13.14 6 17h12l-3.86-5.14z"/>
                                </svg>
                              </PlaceholderIcon>
                            )}
                          </CardImage>
                          <OrderInfo>
                            <OrderHeader>
                              <OrderNumber>#{order.order_number}</OrderNumber>
                              <StatusBadge $color={getStatusColor(order.status)}>
                                {order.status}
                              </StatusBadge>
                            </OrderHeader>
                            <ProductNameText>{order.product_name}</ProductNameText>
                            <OrderDetails>
                              <span>{currency.symbol}{parseFloat(String(order.total_amount)).toFixed(2)}</span>
                              <span>{formatDate(order.created_at)}</span>
                            </OrderDetails>
                            <OrderTags>
                              {order.has_physical_products && <Tag $color="#4CAF50">{t('shop.productTypes.physical')}</Tag>}
                              {order.has_digital_products && <Tag $color="#2196F3">{t('shop.productTypes.downloadable')}</Tag>}
                            </OrderTags>
                          </OrderInfo>
                        </OrderCardContent>
                      </OrderCard>
                    ))}
                  </ItemList>
                )}
              </TabContent>
            )}

            {activeTab === 'downloads' && (
              <TabContent>
                {downloads.length === 0 ? (
                  <EmptyState>
                    <EmptyIcon>
                      <svg width="48" height="48" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z" />
                      </svg>
                    </EmptyIcon>
                    <EmptyText>{t('downloads.noDownloads')}</EmptyText>
                  </EmptyState>
                ) : (
                  <ItemList>
                    {downloads.map((download) => (
                      <DownloadCard key={download.id}>
                        <CardImage>
                          {download.productImage ? (
                            <img src={download.productImage} alt={download.productName} />
                          ) : (
                            <PlaceholderIcon>
                              <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z" />
                              </svg>
                            </PlaceholderIcon>
                          )}
                        </CardImage>
                        <CardContent>
                          <ProductName>{download.productName}</ProductName>
                          <OrderMeta>#{download.orderNumber} • {formatDate(download.createdAt)}</OrderMeta>
                          {download.isExpired && <ExpiredBadge>{t('downloads.expired')}</ExpiredBadge>}
                        </CardContent>
                        <CardAction>
                          <ActionButton
                            onClick={() => handleDownload(download.id, download.productName)}
                            disabled={!download.canDownload}
                          >
                            {t('downloads.download')}
                          </ActionButton>
                        </CardAction>
                      </DownloadCard>
                    ))}
                  </ItemList>
                )}
              </TabContent>
            )}

            {activeTab === 'keys' && (
              <TabContent>
                {keys.length === 0 ? (
                  <EmptyState>
                    <EmptyIcon>
                      <svg width="48" height="48" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12.65 10C11.83 7.67 9.61 6 7 6c-3.31 0-6 2.69-6 6s2.69 6 6 6c2.61 0 4.83-1.67 5.65-4H17v4h4v-4h2v-4H12.65zM7 14c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2z" />
                      </svg>
                    </EmptyIcon>
                    <EmptyText>{t('keys.noKeys')}</EmptyText>
                  </EmptyState>
                ) : (
                  <ItemList>
                    {keys.map((key) => (
                      <KeyCard key={key.id}>
                        <CardImage>
                          {key.productImage ? (
                            <img src={key.productImage} alt={key.productName} />
                          ) : (
                            <PlaceholderIcon>
                              <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M12.65 10C11.83 7.67 9.61 6 7 6c-3.31 0-6 2.69-6 6s2.69 6 6 6c2.61 0 4.83-1.67 5.65-4H17v4h4v-4h2v-4H12.65zM7 14c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2z" />
                              </svg>
                            </PlaceholderIcon>
                          )}
                        </CardImage>
                        <CardContent>
                          <ProductName>{key.productName}</ProductName>
                          <OrderMeta>#{key.orderNumber} • {formatDate(key.createdAt)}</OrderMeta>
                          <KeyValue $revealed={key.isRevealed}>
                            {key.keyValue}
                          </KeyValue>
                        </CardContent>
                        <CardAction>
                          {key.isRevealed ? (
                            <ActionButton onClick={() => copyToClipboard(key.keyValue)}>
                              {t('keys.copy')}
                            </ActionButton>
                          ) : (
                            <ActionButton onClick={() => handleRevealKey(key.id)}>
                              {t('keys.reveal')}
                            </ActionButton>
                          )}
                        </CardAction>
                      </KeyCard>
                    ))}
                  </ItemList>
                )}
              </TabContent>
            )}

            {activeTab === 'subscriptions' && (
              <TabContent>
                {subscriptions.length === 0 ? (
                  <EmptyState>
                    <EmptyIcon>
                      <svg width="48" height="48" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-5 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z" />
                      </svg>
                    </EmptyIcon>
                    <EmptyText>{t('subscriptions.noSubscriptions')}</EmptyText>
                  </EmptyState>
                ) : (
                  <ItemList>
                    {subscriptions.map((sub) => (
                      <SubscriptionCard key={sub.id}>
                        <CardImage>
                          {sub.productImage ? (
                            <img src={sub.productImage} alt={sub.productName} />
                          ) : (
                            <PlaceholderIcon>
                              <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2z" />
                              </svg>
                            </PlaceholderIcon>
                          )}
                        </CardImage>
                        <CardContent>
                          <ProductName>{sub.productName}</ProductName>
                          <OrderMeta>#{sub.orderNumber} • {formatDate(sub.startDate)}</OrderMeta>
                          <SubStatus>
                            <StatusBadge $color={getStatusColor(sub.status)}>
                              {sub.status}
                            </StatusBadge>
                            {!sub.isExpired && (
                              <DaysLeft $warning={sub.isExpiringSoon}>
                                {sub.daysRemaining} {t('subscriptions.daysLeft')}
                              </DaysLeft>
                            )}
                          </SubStatus>
                          <CardMeta>
                            {t('subscriptions.expiresOn')} {formatDate(sub.expiryDate)}
                          </CardMeta>
                        </CardContent>
                        {sub.canRenew && (
                          <CardAction>
                            <ActionButton onClick={() => handleRenewSubscription(sub.id)}>
                              {t('subscriptions.renew')} {currency.symbol}{sub.renewalPrice}
                            </ActionButton>
                          </CardAction>
                        )}
                      </SubscriptionCard>
                    ))}
                  </ItemList>
                )}
              </TabContent>
            )}
          </>
        )}
      </Content>

      <BottomNavigation />
    </PageContainer>
  );
};

const PageContainer = styled.div`
  min-height: 100vh;
  background: var(--tg-theme-secondary-bg-color, #f5f5f5);
  padding-bottom: calc(70px + env(safe-area-inset-bottom, 0px));
  max-width: 768px;
  margin: 0 auto;

  @media (min-width: 768px) {
    max-width: 900px;
  }
`;

const Header = styled.header`
  background: var(--tg-theme-bg-color, #ffffff);
  padding: 12px 16px;
  border-bottom: 1px solid var(--tg-theme-hint-color, #e0e0e0);

  @media (min-width: 375px) {
    padding: 16px;
  }
`;

const Title = styled.h1`
  font-size: 18px;
  font-weight: 600;
  color: var(--tg-theme-text-color, #000000);
  margin: 0;

  @media (min-width: 375px) {
    font-size: 20px;
  }
`;

const TabsContainer = styled.div`
  display: flex;
  background: var(--tg-theme-bg-color, #ffffff);
  border-bottom: 1px solid var(--tg-theme-hint-color, #e0e0e0);
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;

  @media (min-width: 600px) {
    justify-content: center;
    gap: 8px;
  }
`;

const Tab = styled.button<{ $active: boolean }>`
  flex: 1;
  min-width: 70px;
  padding: 10px 6px;
  border: none;
  background: none;
  font-size: 12px;
  font-weight: 500;
  color: ${props => props.$active
    ? 'var(--tg-theme-button-color, #3390ec)'
    : 'var(--tg-theme-hint-color, #999)'};
  border-bottom: 2px solid ${props => props.$active
    ? 'var(--tg-theme-button-color, #3390ec)'
    : 'transparent'};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  transition: all 0.2s;

  @media (min-width: 375px) {
    min-width: 80px;
    padding: 12px 8px;
    font-size: 13px;
  }

  @media (min-width: 600px) {
    min-width: 100px;
    padding: 14px 16px;
    font-size: 14px;
  }
`;

const TabBadge = styled.span`
  background: var(--tg-theme-button-color, #3390ec);
  color: var(--tg-theme-button-text-color, #ffffff);
  font-size: 10px;
  padding: 2px 6px;
  border-radius: 10px;
`;

const Content = styled.main`
  padding: 12px;

  @media (min-width: 375px) {
    padding: 16px;
  }

  @media (min-width: 600px) {
    padding: 20px 24px;
  }
`;

const TabContent = styled.div``;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  padding: 40px;
`;

const Spinner = styled.div`
  width: 32px;
  height: 32px;
  border: 3px solid var(--tg-theme-hint-color, #e0e0e0);
  border-top-color: var(--tg-theme-button-color, #3390ec);
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 40px 20px;
  text-align: center;
`;

const EmptyIcon = styled.div`
  color: var(--tg-theme-hint-color, #999);
  margin-bottom: 12px;
`;

const EmptyText = styled.p`
  color: var(--tg-theme-hint-color, #999);
  font-size: 15px;
  margin: 0;
`;

const ItemList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;

  @media (min-width: 375px) {
    gap: 12px;
  }

  @media (min-width: 600px) {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 16px;
  }
`;

const OrderCard = styled.div`
  background: var(--tg-theme-bg-color, #ffffff);
  border-radius: 10px;
  padding: 10px;
  cursor: pointer;
  transition: transform 0.2s;

  &:active {
    transform: scale(0.98);
  }

  @media (min-width: 375px) {
    border-radius: 12px;
    padding: 12px;
  }

  @media (min-width: 600px) {
    padding: 14px;
  }
`;

const OrderCardContent = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
`;

const OrderInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const ProductNameText = styled.p`
  font-size: 13px;
  color: var(--tg-theme-text-color, #000000);
  margin: 0 0 6px 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const OrderHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 4px;
`;

const OrderNumber = styled.span`
  font-size: 15px;
  font-weight: 600;
  color: var(--tg-theme-text-color, #000000);
`;

const StatusBadge = styled.span<{ $color: string }>`
  background: ${props => props.$color}20;
  color: ${props => props.$color};
  font-size: 11px;
  font-weight: 600;
  padding: 4px 8px;
  border-radius: 4px;
  text-transform: capitalize;
`;

const OrderDetails = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 12px;
  color: var(--tg-theme-hint-color, #999);
  margin-bottom: 6px;
`;

const OrderTags = styled.div`
  display: flex;
  gap: 6px;
`;

const Tag = styled.span<{ $color: string }>`
  background: ${props => props.$color}15;
  color: ${props => props.$color};
  font-size: 10px;
  font-weight: 500;
  padding: 3px 6px;
  border-radius: 4px;
`;

const DownloadCard = styled.div`
  background: var(--tg-theme-bg-color, #ffffff);
  border-radius: 10px;
  padding: 10px;
  display: flex;
  align-items: center;
  gap: 10px;

  @media (min-width: 375px) {
    border-radius: 12px;
    padding: 12px;
    gap: 12px;
  }

  @media (min-width: 600px) {
    padding: 14px;
  }
`;

const KeyCard = styled(DownloadCard)``;

const SubscriptionCard = styled(DownloadCard)`
  flex-wrap: wrap;
`;

const CardImage = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 8px;
  overflow: hidden;
  background: var(--tg-theme-secondary-bg-color, #f0f0f0);
  flex-shrink: 0;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  @media (min-width: 375px) {
    width: 56px;
    height: 56px;
  }

  @media (min-width: 600px) {
    width: 64px;
    height: 64px;
  }
`;

const PlaceholderIcon = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--tg-theme-hint-color, #999);
`;

const CardContent = styled.div`
  flex: 1;
  min-width: 0;
`;

const ProductName = styled.h3`
  font-size: 13px;
  font-weight: 500;
  color: var(--tg-theme-text-color, #000000);
  margin: 0 0 2px 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  @media (min-width: 375px) {
    font-size: 14px;
  }

  @media (min-width: 600px) {
    font-size: 15px;
  }
`;

const OrderMeta = styled.p`
  font-size: 11px;
  color: var(--tg-theme-hint-color, #999);
  margin: 0 0 4px 0;
  opacity: 0.8;
`;

const CardMeta = styled.p`
  font-size: 12px;
  color: var(--tg-theme-hint-color, #999);
  margin: 0;
`;

const CardAction = styled.div`
  flex-shrink: 0;
`;

const ActionButton = styled.button`
  background: var(--tg-theme-button-color, #3390ec);
  color: var(--tg-theme-button-text-color, #ffffff);
  border: none;
  padding: 6px 10px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: opacity 0.2s;
  white-space: nowrap;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &:not(:disabled):active {
    opacity: 0.8;
  }

  @media (min-width: 375px) {
    padding: 8px 14px;
    border-radius: 8px;
    font-size: 13px;
  }

  @media (min-width: 600px) {
    padding: 10px 16px;
    font-size: 14px;
  }
`;

const KeyValue = styled.code<{ $revealed: boolean }>`
  display: block;
  font-size: 10px;
  font-family: monospace;
  background: var(--tg-theme-secondary-bg-color, #f0f0f0);
  padding: 3px 6px;
  border-radius: 4px;
  margin-top: 4px;
  color: ${props => props.$revealed
    ? 'var(--tg-theme-text-color, #000000)'
    : 'var(--tg-theme-hint-color, #999)'};
  word-break: break-all;

  @media (min-width: 375px) {
    font-size: 12px;
    padding: 4px 8px;
  }
`;

const ExpiredBadge = styled.span`
  display: inline-block;
  background: #f4433620;
  color: #f44336;
  font-size: 10px;
  font-weight: 500;
  padding: 2px 6px;
  border-radius: 4px;
  margin-top: 4px;
`;

const SubStatus = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;
`;

const DaysLeft = styled.span<{ $warning: boolean }>`
  font-size: 12px;
  color: ${props => props.$warning ? '#FFA000' : 'var(--tg-theme-hint-color, #999)'};
  font-weight: ${props => props.$warning ? '500' : '400'};
`;

export default Dashboard;
